import { Platform } from 'react-native';
import type * as ExpoPrint from 'expo-print';
import type * as ExpoSharing from 'expo-sharing';
import { File, Paths } from 'expo-file-system';
import { moduleNatifPresent } from './modulesNatifs';
import type { InterviewFeedback, InterviewRole, OutputLanguage } from '../types';

/**
 * PDF DU BILAN D'ENTRETIEN — reprend la page de bilan, rien de plus : aucun contenu inventé.
 *
 * RÉDIGÉ DANS LA LANGUE DE LA SESSION, pas dans celle de l'interface : un entretien mené en anglais
 * donne un bilan anglais, le PDF suit. Les libellés vivent donc ici, par langue de session.
 *
 * `expo-print` et `expo-sharing` se chargent à la demande (`moduleNatifPresent`) : un binaire qui ne
 * les contiendrait pas masque le bouton au lieu de planter au démarrage.
 */

export interface DonneesBilan {
  feedback: InterviewFeedback;
  langue: OutputLanguage;
  role: InterviewRole;
  /** Première ligne du contexte saisi (poste visé), `null` si seule une offre a été fournie. */
  posteVise: string | null;
  aOffre: boolean;
  dureeMinutes: number | null;
  date: Date;
}

const LIBELLES: Record<
  OutputLanguage,
  {
    titre: string;
    date: string;
    poste: string;
    offreFournie: string;
    langue: string;
    nomLangue: string;
    duree: string;
    interlocuteur: string;
    roles: Record<InterviewRole, string>;
    appreciation: string;
    pointsForts: string;
    aAmeliorer: string;
    conseils: string;
    pied: string;
    locale: string;
  }
> = {
  fr: {
    titre: 'Bilan d’entretien',
    date: 'Date',
    poste: 'Poste visé',
    offreFournie: 'Offre fournie',
    langue: 'Langue',
    nomLangue: 'Français',
    duree: 'Durée',
    interlocuteur: 'Interlocuteur',
    roles: { recruiter: 'Recruteur RH', manager: 'Manager' },
    appreciation: 'Appréciation globale',
    pointsForts: 'Points forts',
    aAmeliorer: 'À améliorer',
    conseils: 'Conseils',
    pied: 'Généré par Horméia, Alternjob SAS',
    locale: 'fr-FR',
  },
  en: {
    titre: 'Interview feedback',
    date: 'Date',
    poste: 'Target role',
    offreFournie: 'Job ad provided',
    langue: 'Language',
    nomLangue: 'English',
    duree: 'Duration',
    interlocuteur: 'Interviewer',
    roles: { recruiter: 'HR recruiter', manager: 'Manager' },
    appreciation: 'Overall assessment',
    pointsForts: 'Strengths',
    aAmeliorer: 'To improve',
    conseils: 'Tips',
    pied: 'Generated by Horméia, Alternjob SAS',
    locale: 'en-GB',
  },
};

const echapper = (texte: string) =>
  texte.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const deuxChiffres = (n: number) => String(n).padStart(2, '0');

/** Date locale de l'appareil : c'est le jour où la personne a passé l'entretien. */
export function nomFichierBilan(date: Date): string {
  return `hormeia-feedback-entretien-${date.getFullYear()}-${deuxChiffres(date.getMonth() + 1)}-${deuxChiffres(date.getDate())}.pdf`;
}

function section(titre: string, elements: string[], classe: string): string {
  const lignes = elements.filter((e) => e.trim());
  if (lignes.length === 0) return '';
  return `<section class="${classe}"><h2>${echapper(titre)}</h2><ul>${lignes.map((e) => `<li>${echapper(e)}</li>`).join('')}</ul></section>`;
}

export function construireHtmlBilan(d: DonneesBilan): string {
  const l = LIBELLES[d.langue];
  const poste = d.posteVise ?? (d.aOffre ? l.offreFournie : null);
  const entete: Array<[string, string | null]> = [
    [l.date, d.date.toLocaleDateString(l.locale, { day: 'numeric', month: 'long', year: 'numeric' })],
    [l.poste, poste],
    [l.langue, l.nomLangue],
    [l.duree, d.dureeMinutes !== null ? `${d.dureeMinutes} min` : null],
    [l.interlocuteur, l.roles[d.role]],
  ];
  const fb = d.feedback;
  return `<!DOCTYPE html>
<html lang="${d.langue}">
<head>
<meta charset="utf-8" />
<style>
  @page { size: A4; margin: 18mm 16mm; }
  body { font-family: -apple-system, 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #1B2440; font-size: 11pt; line-height: 1.5; margin: 0; }
  header { border-bottom: 2px solid #2F6BFF; padding-bottom: 10px; margin-bottom: 16px; }
  .marque { font-size: 10pt; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #2F6BFF; }
  h1 { font-size: 20pt; margin: 4px 0 10px; }
  dl { display: grid; grid-template-columns: max-content 1fr; gap: 2px 12px; margin: 0; font-size: 10pt; }
  dt { color: #6B7590; } dd { margin: 0; }
  .appreciation { background: #E7EFFC; border-radius: 8px; padding: 10px 12px; margin-bottom: 14px; }
  h2 { font-size: 12.5pt; margin: 14px 0 6px; }
  ul { margin: 0; padding-left: 18px; } li { margin-bottom: 4px; }
  .forts h2 { color: #1E8E5A; } .ameliorer h2 { color: #B26A00; } .conseils h2 { color: #2F6BFF; }
  footer { margin-top: 24px; padding-top: 8px; border-top: 1px solid #E1E5EE; font-size: 9pt; color: #6B7590; }
  section, .appreciation { page-break-inside: avoid; }
</style>
</head>
<body>
<header>
  <div class="marque">Horméia</div>
  <h1>${echapper(l.titre)}</h1>
  <dl>${entete
    .filter((e): e is [string, string] => e[1] !== null && e[1] !== '')
    .map(([c, v]) => `<dt>${echapper(c)}</dt><dd>${echapper(v)}</dd>`)
    .join('')}</dl>
</header>
${fb.overall.trim() ? `<div class="appreciation"><h2>${echapper(l.appreciation)}</h2><p>${echapper(fb.overall)}</p></div>` : ''}
${section(l.pointsForts, fb.strengths, 'forts')}
${section(l.aAmeliorer, fb.improvements, 'ameliorer')}
${section(l.conseils, fb.tips, 'conseils')}
<footer>${echapper(l.pied)}</footer>
</body>
</html>`;
}

function modules(): { print: typeof ExpoPrint; sharing: typeof ExpoSharing } | null {
  if (Platform.OS !== 'web' && !(moduleNatifPresent('ExpoPrint') && moduleNatifPresent('ExpoSharing'))) return null;
  return {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    print: require('expo-print') as typeof ExpoPrint,
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    sharing: require('expo-sharing') as typeof ExpoSharing,
  };
}

/** Le bouton n'apparaît que si le binaire sait produire et partager un PDF. */
export function pdfBilanDisponible(): boolean {
  return Platform.OS === 'web' || (moduleNatifPresent('ExpoPrint') && moduleNatifPresent('ExpoSharing'));
}

/** Génère le PDF et ouvre le partage (enregistrer dans Fichiers, envoyer…). Sur le web : impression. */
export async function exporterBilanPdf(d: DonneesBilan): Promise<void> {
  const m = modules();
  if (!m) throw new Error('PDF indisponible sur cette version de l’application.');
  const html = construireHtmlBilan(d);
  if (Platform.OS === 'web') {
    await m.print.printAsync({ html });
    return;
  }
  const { uri } = await m.print.printToFileAsync({ html });
  // Renommé pour que le fichier partagé porte un nom lisible, pas l'identifiant d'impression.
  const nomme = new File(Paths.cache, nomFichierBilan(d.date));
  if (nomme.exists) nomme.delete();
  new File(uri).copy(nomme);
  if (!(await m.sharing.isAvailableAsync())) throw new Error('Le partage n’est pas disponible sur cet appareil.');
  await m.sharing.shareAsync(nomme.uri, { mimeType: 'application/pdf', UTI: 'com.adobe.pdf', dialogTitle: nomFichierBilan(d.date) });
}
